import type { Component } from "vue";

import type { Stream } from "shared/types";
import type { BaseMention } from "shared/types/mentions";

export interface MentionCardOptions {
  disableAuthorClick?: boolean;
  hideAfterBodySlot?: boolean;
  hideAfterSlot?: boolean;
  hideExcerpts?: boolean;
  list?: boolean;
  noSquareBorders?: boolean;
  noSquareTopBorder?: boolean;
}

export type MentionCardProps<DefaultMention = BaseMention> = {
  actionSheetComponent: Component;
  alwaysPlayInPopout: boolean;
  alwaysShowKeywords: boolean;
  cardTitleClass: string;
  contentClass: string;
  dark: boolean;
  hideBorderColor: boolean;
  hideEngagement: boolean;
  hideMedia: boolean;
  hideSentiment: boolean;
  icon: string;
  inMentionPreviewAlert: boolean;
  keywords: string[];
  listTitle: string;
  logo: string;
  mention: DefaultMention;
  mentions: DefaultMention[];
  noHighlighting: boolean;
  options: MentionCardOptions;
  product: string;
  selected: boolean;
  showCheckbox: boolean;
  stream: Stream;
  title: string;
};

export const props = {
  actionSheetComponent: {
    default: null,
    type: [Function, Object, String],
  },
  alwaysPlayInPopout: {
    type: Boolean,
  },
  alwaysShowKeywords: {
    type: Boolean,
  },
  baseIcon: {
    default: "",
    type: String,
  },
  cardTitleClass: {
    default: "",
    type: [Array, Object, String],
  },
  contentClass: {
    default: "",
    type: [Array, Object, String],
  },
  dark: {
    type: Boolean,
  },
  hideBorderColor: {
    type: Boolean,
  },
  hideEngagement: {
    type: Boolean,
  },
  hideMedia: {
    type: Boolean,
  },
  hideSentiment: {
    type: Boolean,
  },
  icon: {
    default: "",
    type: String,
  },
  inMentionPreviewAlert: {
    type: Boolean,
  },
  keywords: {
    default: () => [],
    type: Array,
  },
  listTitle: {
    default: "",
    type: String,
  },
  logo: {
    default: "",
    type: String,
  },
  mention: {
    required: true,
    type: Object,
  },
  mentions: {
    default: () => [],
    type: Array,
  },
  noHighlighting: {
    type: Boolean,
  },
  options: {
    default: () => ({}),
    type: Object as () => MentionCardOptions,
  },
  product: {
    default: null,
    type: String,
  },
  selected: {
    type: Boolean,
  },
  showCheckbox: {
    type: Boolean,
  },
  stream: {
    default: () => ({}),
    type: Object,
  },
  title: {
    default: "",
    type: String,
  },
};

export function propsExcept<T extends MentionCardProps>(
  keys: (keyof T)[] = []
): Partial<T> {
  const obj: Partial<T> = {};

  (Object.keys(props) as Array<keyof typeof props>).forEach((key) => {
    if (!keys.includes(key as keyof T)) {
      // TS not happy as props keys !== MentionCardProps keys (force cast here)
      obj[key as keyof T] = props[key] as T[keyof T];
    }
  });

  return obj;
}
